// ControlContentEditor.js
import React, { useMemo } from "react";
import "./ControlContentEditor.css";

export default function ControlContentEditor({
  control,
  lang,
  phraseHasKey,
  phraseGet,
  onChangePhrase,
  showKeyChip,        // true in multi-control mode, false in single-control mode
  isSingle,           // true when only one control is being edited
  pageIndex,          // index of the parent page
  controlIndex,       // index of this control within the page
  hasOptions = false, // true if this control type supports options
  required = false,   // per-control required state (multi mode)
  onToggleRequired,   // (pageIndex, controlIndex, next)
  onAttributesClick,
  onOptionsClick,
  onJsonClick,
}) {
  const { editKey, editValue, isMissing } = useMemo(() => {
    if (!control) {
      return {
        editKey: "",
        editValue: "",
        isMissing: false,
      };
    }

    const keyCandidates = [
      control.label || null,
      control.content || null,
      control.key || null,
    ].filter(Boolean);

    if (keyCandidates.length === 0) {
      return {
        editKey: "",
        editValue: "",
        isMissing: false,
      };
    }

    const hasFn = typeof phraseHasKey === "function";
    const getFn = typeof phraseGet === "function";

    const editKey =
      (hasFn && keyCandidates.find((k) => phraseHasKey(lang, k))) ||
      keyCandidates[keyCandidates.length - 1];

    const hasEntry = hasFn ? phraseHasKey(lang, editKey) : false;
    const value =
      getFn && editKey
        ? phraseGet(lang, editKey) ?? ""
        : "";

    return {
      editKey,
      editValue: value,
      isMissing: !!editKey && !hasEntry,
    };
  }, [control, lang, phraseHasKey, phraseGet]);

  if (!control) return null;

  const rows = isSingle ? 12 : 3; // single-control slightly taller
  const typeLabel = control?.type ? String(control.type).toUpperCase() : "";

  const handleAttributesClick = () => {
    if (typeof onAttributesClick === "function") {
      onAttributesClick(pageIndex, controlIndex);
    }
  };

  const handleOptionsClick = () => {
    if (!hasOptions) return;
    if (typeof onOptionsClick === "function") {
      onOptionsClick(pageIndex, controlIndex);
    }
  };

  const handleJsonClick = () => {
    if (typeof onJsonClick === "function") {
      onJsonClick(pageIndex, controlIndex, control);
    }
  };

  const handleRequiredChange = (next) => {
    if (typeof onToggleRequired === "function") {
      onToggleRequired(pageIndex, controlIndex, !!next);
    }
  };

  const handleTextChange = (e) => {
    const nextValue = e.target.value;
    if (editKey && typeof onChangePhrase === "function") {
      onChangePhrase(editKey, nextValue, lang);
    }
    // In the new architecture, asset tracking happens at a higher level.
    // Dictionary changes here remain immediate via onChangePhrase.
  };

  return (
    <div className="fe-contentitem">
      {/* Per-control header is ONLY shown in multi-control mode */}
      {!isSingle && (
        <div className="fe-contentitem-header">
          <div className="fe-contentitem-left">
            {showKeyChip && editKey && (
              <span
                className={
                  "fe-dictkey " +
                  (isMissing ? "fe-dictkey--missing" : "fe-dictkey--ok")
                }
                title={
                  isMissing
                    ? `Key "${editKey}" is missing in the ${lang.toUpperCase()} dictionary`
                    : `Dictionary key for this content`
                }
              >
                <span className="fe-dictkey-prefix">KEY:</span>
                <code className="fe-dictkey-code">{editKey}</code>
              </span>
            )}

            {typeLabel && (
              <span className="fe-chip fe-chip--type">
                {typeLabel}
              </span>
            )}
          </div>

          <div className="fe-contentitem-right">
            <label className="fe-switch fe-switch--inline" title="Required">
              <input
                type="checkbox"
                checked={!!required}
                onChange={(e) => handleRequiredChange(e.target.checked)}
              />
              Required
            </label>

            <div className="fe-contentitem-actions">
              {/* C removed – content is always the main panel */}
              <button
                type="button"
                className="fe-ctrlicon"
                title="Attributes"
                aria-label="Attributes"
                onClick={handleAttributesClick}
              >
                A
              </button>
              <button
                type="button"
                className="fe-ctrlicon"
                title="Control JSON"
                aria-label="Control JSON"
                onClick={handleJsonClick}
              >
                J
              </button>
              {hasOptions && (
                <button
                  type="button"
                  className="fe-ctrlicon"
                  title="Options"
                  aria-label="Options"
                  onClick={handleOptionsClick}
                >
                  O
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <textarea
        className="fe-textarea"
        rows={rows}
        value={editKey ? editValue : ""}
        onChange={handleTextChange}
        placeholder={editKey ? "" : "No dictionary key on this control"}
      />

      {isMissing && editKey && (
        <div className="fe-dictwarning">
          No phrase exists in the {lang.toUpperCase()} dictionary for{" "}
          <code>{editKey}</code>. Editing here will create or update it for this
          language.
        </div>
      )}
    </div>
  );
}
